import { breakpoints } from './types';
import type { Breakpoint, LayoutAlign, LayoutBorderSide, LayoutFlex, LayoutJustify, LayoutMargin, LayoutSize, LayoutSpace, Responsive } from './types';

export type ByBreakpoint<T> = Partial<Record<Breakpoint, T>>;

/**
 * Attributes to `v-bind` on the element: the custom properties in `style`, plus a
 * `data-<name>[-<breakpoint>]` flag per value. The stylesheet reads a custom property
 * only behind its flag, so a value never leaks into nested layout components through
 * custom-property inheritance.
 */
export type LayoutBindings = { style: Record<string, string> } & { [key: `data-${string}`]: '' };

export function byBreakpoint<T>(value: Responsive<T> | undefined): ByBreakpoint<T> {
    if (value === undefined) return {};
    if (typeof value === 'object' && value !== null) return value as ByBreakpoint<T>;
    return { base: value };
}

/**
 * Walks the breakpoints upwards, carrying each layer's last value, and keeps the result
 * of `pick` only where it changes. Unset breakpoints inherit from the ones below them.
 */
export function resolveLayers<T, R>(layers: (Responsive<T> | undefined)[], pick: (values: (T | undefined)[]) => R | undefined): ByBreakpoint<R> {
    const maps = layers.map((layer) => byBreakpoint(layer));
    const current: (T | undefined)[] = layers.map(() => undefined);
    const result: ByBreakpoint<R> = {};
    let last: R | undefined;
    for (const breakpoint of breakpoints) {
        maps.forEach((map, index) => {
            if (map[breakpoint] !== undefined) current[index] = map[breakpoint];
        });
        const value = pick(current);
        if (value !== undefined && value !== last) {
            result[breakpoint] = value;
            last = value;
        }
    }
    return result;
}

/** Overlapping props, most specific first (`pt`, `py`, `p`): the most specific value set at or below a breakpoint wins */
export function cascade<T>(...layers: (Responsive<T> | undefined)[]): ByBreakpoint<T> {
    return resolveLayers(layers, (values) => values.find((value) => value !== undefined));
}

export function mapBreakpoints<T>(values: ByBreakpoint<T>, toCss: (value: T) => string): ByBreakpoint<string> {
    const result: ByBreakpoint<string> = {};
    for (const breakpoint of breakpoints) {
        const value = values[breakpoint];
        if (value !== undefined) result[breakpoint] = toCss(value);
    }
    return result;
}

export function layoutBindings(vars: Record<string, ByBreakpoint<string>>): LayoutBindings {
    const bindings: LayoutBindings = { style: {} };
    for (const [name, values] of Object.entries(vars)) {
        for (const breakpoint of breakpoints) {
            const value = values[breakpoint];
            if (value === undefined) continue;
            const key = breakpoint === 'base' ? name : `${name}-${breakpoint}`;
            bindings.style[`--ui-layout-${key}`] = value;
            bindings[`data-${key}`] = '';
        }
    }
    return bindings;
}

export const space = (value: LayoutSpace): string => (value === 0 || value === '0' ? '0px' : `var(--ui-layout-space-${value})`);
export const margin = (value: LayoutMargin): string => (value === 'auto' ? 'auto' : space(value));

/** Responsive spacing tokens as CSS values */
export const spaceByBreakpoint = (value: Responsive<LayoutSpace> | undefined) => mapBreakpoints(byBreakpoint(value), space);

const sizeKeywords: Partial<Record<LayoutSize, string>> = {
    full: '100%',
    auto: 'auto',
    fit: 'fit-content',
    min: 'min-content',
    max: 'max-content',
};

/** A size keyword as CSS, or a step of the size scale */
export const size = (value: LayoutSize): string => sizeKeywords[value] ?? `var(--ui-layout-size-${value})`;

const borderWidths: Record<LayoutBorderSide, string> = {
    top: '1px 0 0 0',
    right: '0 1px 0 0',
    bottom: '0 0 1px 0',
    left: '0 0 0 1px',
    x: '0 1px',
    y: '1px 0',
};

/** `border-width` for a side or a pair; all four sides without one */
export const borderWidth = (side: LayoutBorderSide | undefined): string => (side === undefined ? '1px' : borderWidths[side]);

export const flexValues: Record<LayoutFlex, string> = {
    fill: '1 1 0',
    auto: '1 1 auto',
    none: 'none',
};

const alignments: Record<LayoutAlign, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
    baseline: 'baseline',
};

const justifications: Record<LayoutJustify, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
    around: 'space-around',
    evenly: 'space-evenly',
};

/** A non-responsive value as CSS, or nothing */
export const single = <T>(value: T | undefined, toCss: (value: T) => string): ByBreakpoint<string> => (value === undefined ? {} : { base: toCss(value) });
export const alignItems = (value: LayoutAlign | undefined) => single(value, (item) => alignments[item]);
export const justifyContent = (value: LayoutJustify | undefined) => single(value, (item) => justifications[item]);
